import { Tooltip } from "primereact/tooltip";
import { CSSProperties, useEffect, useId, useRef, useState } from "react";

type TruncatedTitleProps = {
  title: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Renders a single-line title that truncates with an ellipsis when it does not
 * fit the available width. The full title is shown in a tooltip only while the
 * text is actually truncated. Re-checks on container resize.
 */
const TruncatedTitle = ({ title, className, style }: TruncatedTitleProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const targetId = "truncated-title-" + useId().replace(/:/g, "");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkTruncation = () =>
      setIsTruncated(el.scrollWidth > el.clientWidth);

    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [title]);

  return (
    <>
      {isTruncated && <Tooltip target={`#${targetId}`} position="bottom" />}
      <span
        id={targetId}
        ref={ref}
        className={className}
        data-pr-tooltip={isTruncated ? title : undefined}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...style,
        }}
      >
        {title}
      </span>
    </>
  );
};

export default TruncatedTitle;
